import { useState, useEffect, useRef } from 'react';
import { trackCTAClick } from '@/lib/analytics';
import { useTranslation } from 'react-i18next';

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ behavior: prefersReduced ? 'instant' : 'smooth', block: 'start' });
};

const DOMAIN_COLORS = ['#d6a84a', '#c9972f', '#d6a84a', '#e8c36a', '#c9972f'];
const DOMAIN_PCTS = [72, 58, 85, 45, 62];

export const PreviewCard = () => {
  const { t } = useTranslation();
  const [animated, setAnimated] = useState(false);
  const sectionRef = useRef(null);
  const prefersReduced = typeof window !== 'undefined'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  useEffect(() => {
    if (prefersReduced) { setAnimated(true); return; }
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setAnimated(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [prefersReduced]);

  const domains = [1, 2, 3, 4, 5].map((n, i) => ({
    name: t(`preview.d${n}_name`),
    desc: t(`preview.d${n}_desc`),
    pct: DOMAIN_PCTS[i],
    color: DOMAIN_COLORS[i],
  }));

  return (
    <section
      data-testid="preview-card-section"
      className="py-16 lg:py-24 px-6 sm:px-8"
      style={{ backgroundColor: 'var(--vc-bg-2)' }}
    >
      <div className="max-w-[1160px] mx-auto">
        <div className="text-center mb-12">
          <h2
            className="text-2xl sm:text-3xl lg:text-[34px] font-bold tracking-tight"
            style={{ fontFamily: 'Inter, sans-serif', color: 'var(--vc-fg)' }}
          >
            {t('preview.title')}
          </h2>
          <p
            className="mt-4 text-base leading-relaxed max-w-md mx-auto"
            style={{ color: 'var(--vc-fg-muted)', fontFamily: 'Inter, sans-serif' }}
          >
            {t('preview.subtitle')}
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <div
            className="rounded-[24px] overflow-hidden"
            style={{
              backgroundColor: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
            }}
          >
            {/* Top: Age comparison */}
            <div
              className="p-8 sm:p-10 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12"
              style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
            >
              <div className="text-center">
                <p className="text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--vc-fg-subtle)', fontFamily: 'Inter, sans-serif' }}>
                  {t('preview.calendar_age')}
                </p>
                <p className="text-4xl sm:text-5xl font-bold" style={{ fontFamily: 'Inter, sans-serif', color: 'var(--vc-fg-muted)' }}>
                  42
                </p>
              </div>
              <div className="text-2xl" style={{ color: 'var(--vc-gold)' }}>{"\u2192"}</div>
              <div className="text-center">
                <p className="text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--vc-fg-subtle)', fontFamily: 'Inter, sans-serif' }}>
                  {t('preview.bio_age')}
                </p>
                <p className="text-4xl sm:text-5xl font-bold" style={{ fontFamily: 'Inter, sans-serif', color: 'var(--vc-gold)' }}>
                  38.5
                </p>
              </div>
              <div
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium relative"
                style={{
                  backgroundColor: 'rgba(34,197,94,0.12)',
                  color: '#4ade80',
                  border: '1px solid rgba(34,197,94,0.2)',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                <span className="absolute inset-0 rounded-full animate-ping" style={{ backgroundColor: 'rgba(34,197,94,0.08)', animationDuration: '2s' }} />
                <span className="relative">{t('preview.years_younger')}</span>
              </div>
            </div>

            {/* Bottom: Health domains */}
            <div ref={sectionRef} className="p-8 sm:p-10">
              <p className="text-[10px] uppercase tracking-[0.15em] mb-6" style={{ color: 'var(--vc-fg-subtle)', fontFamily: 'Inter, sans-serif' }}>
                {t('preview.domain_analysis')}
              </p>
              <div className="space-y-5">
                {domains.map((d, i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div>
                        <span className="text-sm font-medium" style={{ color: 'var(--vc-fg)', fontFamily: 'Inter, sans-serif' }}>{d.name}</span>
                        <span className="text-xs ml-2" style={{ color: 'var(--vc-fg-subtle)', fontFamily: 'Inter, sans-serif' }}>{d.desc}</span>
                      </div>
                      <span
                        className="text-sm font-medium"
                        style={{
                          color: d.color,
                          fontFamily: 'Inter, sans-serif',
                          opacity: animated ? 1 : 0,
                          transition: prefersReduced ? 'none' : `opacity 400ms ease ${200 + i * 120}ms`,
                        }}
                      >
                        {d.pct}%
                      </span>
                    </div>
                    <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: animated ? `${d.pct}%` : '0%',
                          backgroundColor: d.color,
                          opacity: 0.85,
                          transition: prefersReduced ? 'none' : `width 800ms cubic-bezier(0.22, 1, 0.36, 1) ${200 + i * 120}ms`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center mt-8">
            <button
              onClick={() => { trackCTAClick('preview_card_cta'); scrollToSection('waitlist'); }}
              className="inline-flex items-center justify-center h-12 px-8 text-sm font-medium rounded-xl transition-colors duration-200 active:scale-[0.98]"
              style={{ backgroundColor: 'var(--vc-gold)', color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--vc-gold-2)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--vc-gold)'}
            >
              {t('preview.cta')}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
